import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

/**
 * Device ping indicator — colored dot + relative time.
 *
 * Thresholds (per user spec):
 *   Green  (pulse): < 30 minutes
 *   Amber:          30–60 minutes
 *   Red:            > 60 minutes (1+ hours)
 *
 * Shows "No data" if lastPing is null/undefined.
 */

interface Props {
    lastPing: Date | string | null | undefined;
}

export const PingIndicator: React.FC<Props> = ({ lastPing }) => {
    const theme = useTheme();

    if (!lastPing) {
        return (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                No data
            </Typography>
        );
    }

    const pingDate = dayjs(lastPing);
    const minutesAgo = dayjs().diff(pingDate, 'minute');

    let dotColor: string;
    let shouldPulse = false;

    if (minutesAgo < 30) {
        dotColor = theme.palette.success.main;
        shouldPulse = true;
    } else if (minutesAgo < 60) {
        dotColor = theme.palette.warning.main;
    } else {
        dotColor = theme.palette.error.main;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: dotColor,
                    flexShrink: 0,
                    ...(shouldPulse && {
                        animation: 'pingPulse 2s ease-in-out infinite',
                        '@keyframes pingPulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                        },
                    }),
                }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {pingDate.fromNow()}
            </Typography>
        </Box>
    );
};
